import React, { FC, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IFinance } from "../../../helpers/types";

import "./FinanceForm.scss";
import RectButton from "../../atoms/RectButton/RectButton";

import Select from "../../atoms/Select/Select";
import DateInputGradient from "../../atoms/DateInputGradient/DateInputGradient";
import { dateStringFromTsUTC, tsFromDateStringUTC } from "../../../helpers/functions";
import {cities, currencies, salaryNames, transferDescriptionItems} from "../../../helpers/constants";
import Input from "../../molecules/Input/Input";

interface FinanceFormProps {
    initialValues?: Partial<IFinance>;
    onSubmit: (data: Partial<IFinance>) => void | Promise<void>;
    submitLabel: string;
    onCancel?: () => void;
}

interface FinanceFormValues {
    date: string;
    name: string;

    send_amount: string;
    transfer_amount: string;

    rub_amount: string;
    purchase_currency: string;

    description: string;
    description_select: string;
    city: string;
    transaction_type: string;

    salary_amount: string;
    expense_amount: string;

    profit_rub: string;
    purchase_amount: string;

    purchase_amount_currency: string;
}

const DEFAULT_PURCHASE_AMOUNT_CURRENCY = "USDT";
const DEFAULT_TRANSFER_CURRENCY = "USDT";

const isOnlineCity = (city: string) => /online/i.test(city);

const validationSchema = Yup.object({
    transaction_type: Yup.string()
        .oneOf(["TRANSFER", "SALARY", "EXPENSES"], "Неверный тип транзакции")
        .required("Обязательное поле"),
    date: Yup.string().required("Обязательное поле"),
    name: Yup.string().when("transaction_type", {
        is: (val: string) => ["TRANSFER", "SALARY", "EXPENSES"].includes(val),
        then: schema => schema.required("Обязательное поле"),
        otherwise: schema => schema
    }),

    send_amount: Yup.string().when("transaction_type", {
        is: "TRANSFER",
        then: schema =>
            schema
                .required("Обязательное поле")
                .matches(/^\d+(\.\d+)?$/, "Должно быть числом"),
        otherwise: schema => schema.notRequired()
    }),

    transfer_amount: Yup.string().when("transaction_type", {
        is: "TRANSFER",
        then: schema =>
            schema
                .required("Обязательное поле")
                .matches(/^\d+(\.\d+)?$/, "Должно быть числом"),
        otherwise: schema => schema.notRequired()
    }),

    rub_amount: Yup.string().when(["transaction_type", "city"], {
        is: (type: string, city: string) => type === "TRANSFER" && isOnlineCity(city || ""),
        then: schema => schema.notRequired(),
        otherwise: schema =>
            schema.when("transaction_type", {
                is: "TRANSFER",
                then: s =>
                    s
                        .required("Обязательное поле")
                        .matches(/^\d+(\.\d+)?$/, "Должно быть числом"),
                otherwise: s => s.notRequired()
            })
    }),

    salary_amount: Yup.string().when("transaction_type", {
        is: "SALARY",
        then: schema =>
            schema
                .required("Обязательное поле")
                .matches(/^\d+(\.\d+)?$/, "Должно быть числом"),
        otherwise: schema => schema.notRequired()
    }),

    expense_amount: Yup.string().when("transaction_type", {
        is: "EXPENSES",
        then: schema =>
            schema
                .required("Обязательное поле")
                .matches(/^\d+(\.\d+)?$/, "Должно быть числом"),
        otherwise: schema => schema.notRequired()
    }),

    profit_rub: Yup.string().when("transaction_type", {
        is: "TRANSFER",
        then: schema =>
            schema
                .required("Обязательное поле")
                .matches(/^\d+(\.\d+)?$/, "Должно быть числом"),
        otherwise: schema => schema.notRequired()
    }),

    purchase_amount: Yup.string().when("transaction_type", {
        is: "TRANSFER",
        then: schema =>
            schema
                .required("Обязательное поле")
                .matches(/^\d+(\.\d+)?$/, "Должно быть числом"),
        otherwise: schema => schema.notRequired()
    }),

    purchase_currency: Yup.string().when("transaction_type", {
        is: "TRANSFER",
        then: schema => schema.required("Обязательное поле"),
        otherwise: schema => schema.notRequired()
    }),

    city: Yup.string().when("transaction_type", {
        is: (val: string) => val === "TRANSFER" || val === "EXPENSES",
        then: schema => schema.required("Обязательное поле"),
        otherwise: schema => schema.notRequired()
    }),

    description: Yup.string().notRequired(),
    description_select: Yup.string().notRequired()
});

const FinanceForm: FC<FinanceFormProps> = ({ initialValues, onSubmit, submitLabel, onCancel }) => {
    const initialDescription = initialValues?.description || "";
    const isPresetDescription = transferDescriptionItems.some(i => i.value === initialDescription);
    const initialDescriptionSelect =
        initialValues?.transaction_type === "TRANSFER"
            ? initialDescription
                ? isPresetDescription
                    ? initialDescription
                    : "OTHER"
                : ""
            : "";

    const formik = useFormik<FinanceFormValues>({
        initialValues: {
            date: dateStringFromTsUTC(initialValues?.date),
            name: initialValues?.name || "",

            send_amount:
                (initialValues as any)?.send_amount !== undefined && (initialValues as any)?.send_amount !== null
                    ? String((initialValues as any).send_amount)
                    : "",

            transfer_amount:
                initialValues?.transfer_amount !== undefined && initialValues?.transfer_amount !== null
                    ? String(initialValues.transfer_amount)
                    : "",

            rub_amount:
                initialValues?.rub_amount !== undefined && initialValues?.rub_amount !== null
                    ? String(initialValues.rub_amount)
                    : "",

            purchase_currency: initialValues?.purchase_currency || DEFAULT_TRANSFER_CURRENCY,

            description: initialDescription,
            description_select: initialDescriptionSelect,
            city: initialValues?.city || "",
            transaction_type: initialValues?.transaction_type || "TRANSFER",

            salary_amount:
                initialValues?.transaction_type === "SALARY" &&
                initialValues?.rub_amount !== undefined &&
                initialValues?.rub_amount !== null
                    ? String(initialValues.rub_amount)
                    : "",

            expense_amount:
                initialValues?.transaction_type === "EXPENSES" &&
                initialValues?.rub_amount !== undefined &&
                initialValues?.rub_amount !== null
                    ? String(initialValues.rub_amount)
                    : "",

            profit_rub:
                initialValues?.profit_rub !== undefined && initialValues?.profit_rub !== null
                    ? String(initialValues.profit_rub)
                    : "",

            purchase_amount:
                initialValues?.purchase_amount !== undefined && initialValues?.purchase_amount !== null
                    ? String(initialValues.purchase_amount)
                    : "",

            purchase_amount_currency: DEFAULT_PURCHASE_AMOUNT_CURRENCY
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (formValues, { resetForm, setSubmitting }) => {
            const transferDescription =
                formValues.description_select === "OTHER"
                    ? formValues.description
                    : formValues.description_select;

            const descriptionValue =
                formValues.transaction_type === "TRANSFER"
                    ? transferDescription
                    : formValues.description;

            const payload: Partial<IFinance> & { purchase_amount_currency?: string; send_amount?: number } = {
                date: tsFromDateStringUTC(formValues.date),
                name: formValues.name,

                send_amount: formValues.transaction_type === "TRANSFER" ? Number(formValues.send_amount) : undefined,

                transfer_amount: formValues.transfer_amount ? Number(formValues.transfer_amount) : undefined,
                rub_amount: formValues.rub_amount ? Number(formValues.rub_amount) : undefined,

                purchase_currency: formValues.purchase_currency as any,

                description: descriptionValue ? descriptionValue : undefined,
                city: formValues.city,
                transaction_type: formValues.transaction_type as any,

                purchase_amount: formValues.purchase_amount ? Number(formValues.purchase_amount) : undefined,
                profit_rub: formValues.profit_rub ? Number(formValues.profit_rub) : undefined,

                purchase_amount_currency:
                    formValues.transaction_type === "TRANSFER" ? DEFAULT_PURCHASE_AMOUNT_CURRENCY : undefined
            };

            if (formValues.transaction_type === "SALARY") {
                payload.rub_amount = Number(formValues.salary_amount);
            }

            if (formValues.transaction_type === "EXPENSES") {
                payload.rub_amount = Number(formValues.expense_amount);
            }

            // Для Online-города rub_amount = 0 если не заполнено
            if (formValues.transaction_type === "TRANSFER" && isOnlineCity(formValues.city)) {
                payload.rub_amount = formValues.rub_amount ? Number(formValues.rub_amount) : 0;
            }

            try {
                await onSubmit(payload);
                resetForm();
                if (onCancel) onCancel();
            } catch {
            } finally {
                setSubmitting(false);
            }
        }
    });

    const { values, handleChange, handleBlur, handleSubmit, resetForm, setFieldValue, errors, touched } = formik;

    const isProfitManualRef = useRef(false);

    // *** ИЗМЕНЕНИЕ: Ref для отслеживания предыдущего города ***
    const prevCityRef = useRef(values.city);

    useEffect(() => {
        isProfitManualRef.current =
            initialValues?.transaction_type === "TRANSFER" &&
            initialValues?.profit_rub !== undefined &&
            initialValues?.profit_rub !== null;
    }, [initialValues?.transaction_type, initialValues?.profit_rub]);

    // *** ИЗМЕНЕНИЕ: При выборе Online-города автоматически ставим rub_amount = "0" ***
    useEffect(() => {
        if (values.transaction_type !== "TRANSFER") return;

        const wasOnline = isOnlineCity(prevCityRef.current);
        const nowOnline = isOnlineCity(values.city);

        // Ставим "0" только при переключении НА Online (не при каждом рендере)
        if (!wasOnline && nowOnline) {
            setFieldValue("rub_amount", "0", false);
        }

        prevCityRef.current = values.city;
    }, [values.city, values.transaction_type, setFieldValue]);

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        isProfitManualRef.current = false;
        handleChange(e);
    };

    const handleProfitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        isProfitManualRef.current = true;
        handleChange(e);
    };

    useEffect(() => {
        if (values.transaction_type !== "TRANSFER") return;
        if (isProfitManualRef.current) return;

        const purchaseStr = values.purchase_amount;
        const sendStr = values.send_amount;

        const purchase = Number(purchaseStr);
        const send = Number(sendStr);

        const purchaseOk = purchaseStr !== "" && Number.isFinite(purchase);
        const sendOk = sendStr !== "" && Number.isFinite(send);

        if (!purchaseOk || !sendOk) {
            if (values.profit_rub !== "") setFieldValue("profit_rub", "", false);
            return;
        }

        const profit = Math.round((purchase - send) * 1e8) / 1e8;
        const profitStr = String(profit);

        if (values.profit_rub !== profitStr) {
            setFieldValue("profit_rub", profitStr, false);
        }
    }, [values.transaction_type, values.purchase_amount, values.send_amount, values.profit_rub, setFieldValue]);

    const handleCancelClick = () => {
        resetForm();
        if (onCancel) onCancel();
    };

    const getError = (field: keyof FinanceFormValues) => (touched[field] && errors[field] ? errors[field] : "");

    return (
        <form className="finance-form">
            <div className="finance-form__row">
                {values.transaction_type === "TRANSFER" && (
                    <>
                        <div className="finance-form__field">
                            <Input
                                name="name"
                                placeholder="Название"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("name")}
                            />
                        </div>

                        <div className="finance-form__field">
                            <Input
                                type={'number'}
                                name="rub_amount"
                                placeholder="Сумма в рублях"
                                value={values.rub_amount}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("rub_amount")}
                            />
                        </div>

                        <div className="finance-form__field finance-form__purchase-amount">
                            <Input
                                type={'number'}
                                name="purchase_amount"
                                placeholder="Сумма закупки"
                                value={values.purchase_amount}
                                onChange={handleAmountChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("purchase_amount")}
                            />
                            <span className="finance-form__purchase-amount-currency">{DEFAULT_PURCHASE_AMOUNT_CURRENCY}</span>
                        </div>

                        <div className="finance-form__field finance-form__field--select">
                            <Select
                                name="city"
                                placeholder="Город"
                                items={cities.map(c => ({ value: c, name: c }))}
                                defaultValue={values.city}
                                onChange={value => setFieldValue("city", value)}
                                type="search"
                            />
                            {touched.city && errors.city && <p style={{ color: "#DB3522", fontSize: "12px" }}>{errors.city}</p>}
                        </div>

                        <div className="finance-form__field finance-form__purchase-amount">
                            <Input
                                type={'number'}
                                name="transfer_amount"
                                placeholder="Сумма перевода"
                                value={values.transfer_amount}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("transfer_amount")}
                            />
                            <span className="finance-form__purchase-amount-currency">
                                {values.purchase_currency || DEFAULT_TRANSFER_CURRENCY}
                            </span>
                        </div>

                        <div className="finance-form__field finance-form__purchase-amount">
                            <Input
                                type={'number'}
                                name="send_amount"
                                placeholder="Сумма отправки"
                                value={values.send_amount}
                                onChange={handleAmountChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("send_amount")}
                            />
                            <span className="finance-form__purchase-amount-currency">{DEFAULT_TRANSFER_CURRENCY}</span>
                        </div>

                        <div className="finance-form__field finance-form__field--select">
                            <Select
                                name="description_select"
                                placeholder="Описание"
                                items={transferDescriptionItems}
                                defaultValue={values.description_select}
                                onChange={value => {
                                    setFieldValue("description_select", value);
                                    if (value !== "OTHER") {
                                        setFieldValue("description", "");
                                    }
                                }}
                            />
                        </div>

                        {values.description_select === "OTHER" && (
                            <div className="finance-form__field">
                                <Input
                                    name="description"
                                    placeholder="Описание"
                                    value={values.description}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    ErrorMessage={getError("description")}
                                />
                            </div>
                        )}

                        <div className="finance-form__field finance-form__field--select">
                            <Select
                                name="purchase_currency"
                                placeholder="Валюта перевода"
                                items={currencies}
                                defaultValue={values.purchase_currency}
                                onChange={value => setFieldValue("purchase_currency", value)}
                            />
                            {touched.purchase_currency && errors.purchase_currency && (
                                <p style={{ color: "#DB3522", fontSize: "12px" }}>{errors.purchase_currency}</p>
                            )}
                        </div>

                        <div className="finance-form__field">
                            <Input
                                type={'number'}
                                name="profit_rub"
                                placeholder="Прибыль USDT"
                                value={values.profit_rub}
                                onChange={handleProfitChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("profit_rub")}
                            />
                        </div>
                    </>
                )}

                {values.transaction_type === "SALARY" && (
                    <>
                        <div className="finance-form__field finance-form__field--select">
                            <Select
                                name="name"
                                placeholder="Имя"
                                items={salaryNames}
                                defaultValue={values.name}
                                onChange={value => setFieldValue("name", value)}
                            />
                            {touched.name && errors.name && <p style={{ color: "#DB3522", fontSize: "12px" }}>{errors.name}</p>}
                        </div>

                        <div className="finance-form__field">
                            <Input
                                name="description"
                                placeholder="Описание"
                                value={values.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("description")}
                            />
                        </div>

                        <div className="finance-form__field finance-form__purchase-amount">
                            <Input
                                type={'number'}
                                name="salary_amount"
                                placeholder="Сумма"
                                value={values.salary_amount}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("salary_amount")}
                            />
                            <span className="finance-form__purchase-amount-currency">{DEFAULT_PURCHASE_AMOUNT_CURRENCY}</span>
                        </div>
                    </>
                )}

                {values.transaction_type === "EXPENSES" && (
                    <>
                        <div className="finance-form__field">
                            <Input
                                name="name"
                                placeholder="Название"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("name")}
                            />
                        </div>

                        <div className="finance-form__field finance-form__purchase-amount">
                            <Input
                                type={'number'}
                                name="expense_amount"
                                placeholder="Сумма"
                                value={values.expense_amount}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("expense_amount")}
                            />
                            <span className="finance-form__purchase-amount-currency">{DEFAULT_PURCHASE_AMOUNT_CURRENCY}</span>
                        </div>

                        <div className="finance-form__field finance-form__field--select">
                            <Select
                                name="city"
                                placeholder="Город"
                                items={cities.map(c => ({ value: c, name: c }))}
                                defaultValue={values.city}
                                onChange={value => setFieldValue("city", value)}
                                type="search"
                            />
                            {touched.city && errors.city && <p style={{ color: "#DB3522", fontSize: "12px" }}>{errors.city}</p>}
                        </div>

                        <div className="finance-form__field">
                            <Input
                                name="description"
                                placeholder="Описание"
                                value={values.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                ErrorMessage={getError("description")}
                            />
                        </div>
                    </>
                )}

                <div className="finance-form__field">
                    <DateInputGradient
                        name="date"
                        placeholder="Дата"
                        value={values.date}
                        onChange={value => setFieldValue("date", value)}
                    />
                    {touched.date && errors.date && <p style={{ color: "#DB3522", fontSize: "12px" }}>{errors.date}</p>}
                </div>
            </div>

            <div className="finance-form__actions">
                <RectButton onClick={handleSubmit} type="button" name={submitLabel} />
                {onCancel && (
                    <RectButton name="Cancel" className="finance-form__cancel" onClick={handleCancelClick} />
                )}
            </div>
        </form>
    );
};

export default FinanceForm;